import { useCallback, useEffect, useMemo, useState } from 'react';

import { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import { useLocale } from '@shared/hooks/locale';
import { fromGlobalId, toGlobalId } from '@shared/utils/global-id';

export const DOCUMENT_ID_PREFIX = {
  CMAReport: 'CMAReport',
  Lead: 'Lead',
  Lot: 'Lot',
} as const;

const THEME_FILE_BODY_TYPE: Partial<
  Record<Doc_Template_Type_Enum_Enum, Doc_Template_Type_Enum_Enum>
> = {
  [Doc_Template_Type_Enum_Enum.CmaReportTheme]:
    Doc_Template_Type_Enum_Enum.CmaReport,
  [Doc_Template_Type_Enum_Enum.LotBrochureTheme]:
    Doc_Template_Type_Enum_Enum.LotBrochure,
};

export const isThemeFileType = (
  templateType: Doc_Template_Type_Enum_Enum | undefined,
) => templateType != null && templateType in THEME_FILE_BODY_TYPE;

export const useThemeReports = () => {
  const { t } = useLocale();

  return useMemo(
    () => [
      { type: Doc_Template_Type_Enum_Enum.CmaReportTheme, label: t('CMA') },
      {
        type: Doc_Template_Type_Enum_Enum.LotBrochureTheme,
        label: t('Listing Brochure'),
      },
    ],
    [t],
  );
};

export const getPreviewTemplateType = (
  templateType: Doc_Template_Type_Enum_Enum | undefined,
) =>
  templateType == null
    ? undefined
    : THEME_FILE_BODY_TYPE[templateType] ?? templateType;

export const getIdPrefixFromTemplateType = (
  templateType: Doc_Template_Type_Enum_Enum | undefined,
): keyof typeof DOCUMENT_ID_PREFIX | null => {
  switch (getPreviewTemplateType(templateType)) {
    case Doc_Template_Type_Enum_Enum.LotBrochure:
    case Doc_Template_Type_Enum_Enum.SellerReport:
      return DOCUMENT_ID_PREFIX.Lot;
    case Doc_Template_Type_Enum_Enum.AppraisalReport:
      return DOCUMENT_ID_PREFIX.Lead;
    case Doc_Template_Type_Enum_Enum.CmaReport:
      return DOCUMENT_ID_PREFIX.CMAReport;
    default:
      return null;
  }
};

export const isThemeTemplateType = (
  templateType: Doc_Template_Type_Enum_Enum | undefined,
) =>
  templateType === Doc_Template_Type_Enum_Enum.Theme ||
  isThemeFileType(templateType);

const getLocalStorageKey = (
  templateType: Doc_Template_Type_Enum_Enum | undefined,
) => {
  const previewType = getPreviewTemplateType(templateType);

  return previewType ? `${previewType}_globalDocumentId` : null;
};

export function usePersistentPreviewId(
  templateType: Doc_Template_Type_Enum_Enum | undefined,
) {
  const [globalId, setGlobalIdState] = useState<string | null>(null);

  useEffect(() => {
    const key = getLocalStorageKey(templateType);
    let storedId = null;
    if (key) {
      try {
        storedId = localStorage.getItem(key);
      } catch (error) {
        console.error('Error reading preview ID from localStorage:', error);
      }
    }
    setGlobalIdState(storedId);
  }, [templateType]);

  const setPersistentGlobalId = useCallback(
    (newGlobalId: string | null) => {
      setGlobalIdState(newGlobalId);
      const key = getLocalStorageKey(templateType);
      if (key) {
        try {
          if (newGlobalId == null || newGlobalId === '') {
            localStorage.removeItem(key);
          } else {
            fromGlobalId(newGlobalId);
            localStorage.setItem(key, newGlobalId);
          }
        } catch (error) {
          console.error('Error writing preview ID to localStorage:', error);
          if (newGlobalId != null && newGlobalId !== '') {
            try {
              localStorage.removeItem(key);
            } catch (removeError) {
              console.error(
                'Error removing invalid preview ID from localStorage:',
                removeError,
              );
            }
          }
        }
      }
    },
    [templateType],
  );

  const documentId = useMemo(() => {
    if (!globalId) {
      return '';
    }
    try {
      return fromGlobalId(globalId) ?? '';
    } catch (error) {
      if (globalId) {
        console.warn('Error decoding global ID for preview:', globalId, error);
      }
      return '';
    }
  }, [globalId]);

  const setDocumentId = useCallback(
    (id: string | null) => {
      const prefix = getIdPrefixFromTemplateType(templateType);
      if (id == null || id.trim() === '' || !prefix) {
        setPersistentGlobalId(null);
      } else {
        try {
          const newGlobalId = toGlobalId(prefix, id.trim());
          setPersistentGlobalId(newGlobalId);
        } catch (error) {
          console.error(
            'Error encoding global ID for preview:',
            prefix,
            id,
            error,
          );
          setPersistentGlobalId(null);
        }
      }
    },
    [templateType, setPersistentGlobalId],
  );

  return {
    documentId,
    globalDocumentId: globalId,
    setDocumentId,
    setGlobalDocumentId: setPersistentGlobalId,
  };
}

export const useOpenTemplateEditorInNewTab = () => {
  const { language } = useLocale();

  return useCallback(
    (
      type: Doc_Template_Type_Enum_Enum,
      templateId: string,
      documentId: string,
    ) => {
      const key = getLocalStorageKey(type);
      if (key) {
        localStorage.setItem(key, documentId);
      }

      window.open(
        `/${language}/settings/doc-templates/${templateId}/edit`,
        '_blank',
      );
    },
    [language],
  );
};

export const sortTranslations = (
  translations: Record<string, Record<string, string>>,
): Record<string, Record<string, string>> =>
  Object.fromEntries(
    Object.entries(translations).map(([lang, keys]) => [
      lang,
      Object.fromEntries(
        Object.entries(keys).sort(([a], [b]) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' }),
        ),
      ),
    ]),
  );

export const createFocusedTranslationView = (
  fullText: string,
  translationKey: string,
): string => {
  try {
    const parsedTranslations = JSON.parse(fullText);
    const focusedView = Object.entries(parsedTranslations)
      .map(([lang, translations]: [string, any]) => {
        const value = translations[translationKey] ?? '__undefined__';
        return `"${lang}": {\n\t"${translationKey}": "${value}"\n}`;
      })
      .join(',\n');

    return `{\n${focusedView}\n}`;
  } catch (e) {
    console.error('Error creating focused view:', e);
    return fullText;
  }
};

export const updateFullTranslations = (
  fullText: string,
  focusedText: string,
  translationKey: string,
): string => {
  try {
    const fullTranslations = JSON.parse(fullText);
    const focusedTranslations = JSON.parse(focusedText);

    // Update the full translations with values from the focused view
    Object.entries(focusedTranslations).forEach(
      ([lang, translations]: [string, any]) => {
        const value = translations[translationKey];
        if (value && value !== '__undefined__') {
          fullTranslations[lang][translationKey] = value;
        }
      },
    );

    return JSON.stringify(fullTranslations, null, 2);
  } catch (e) {
    console.error('Error updating full translations:', e);
    return fullText;
  }
};
